import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { LogOut, User } from 'lucide-react';
import BookingsManagement from '../components/admin/BookingsManagement';
import ToursManagement from '../components/admin/ToursManagement';
import BlogManagement from '../components/admin/BlogManagement';
import ContactsManagement from '../components/admin/ContactsManagement';
import SliderManagement from '../components/admin/SliderManagement';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import SeoSettings from '../components/admin/SeoSettings';
import api from '../services/api';
import { toast } from 'sonner';
import SEO from '../components/SEO';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const [adminName, setAdminName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await api.get('/auth/verify', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.valid) {
        setAdminName(response.data.name || localStorage.getItem('adminName') || 'Admin');
        setLoading(false);
      } else {
        throw new Error('Invalid token');
      }
    } catch (error) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminName');
      navigate('/admin/login');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminName');
    toast.success('Çıkış yapıldı');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <SEO title="Yönetim" noindex />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-dashboard-title">
              Yönetim Paneli
            </h1>
            <p className="text-gray-600">Turlar, rezervasyonlar ve içerik yönetimi</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-600 bg-white px-4 py-2 rounded-full">
              <User size={18} />
              <span className="font-medium">{adminName}</span>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="text-red-500 border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Çıkış
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="flex flex-wrap h-auto w-full max-w-4xl bg-white justify-start">
            <TabsTrigger value="bookings" className="font-bold" data-testid="tab-bookings">
              Rezervasyonlar
            </TabsTrigger>
            <TabsTrigger value="tours" className="font-bold" data-testid="tab-tours">
              Turlar
            </TabsTrigger>
            <TabsTrigger value="slider" className="font-bold" data-testid="tab-slider">
              Slider
            </TabsTrigger>
            <TabsTrigger value="blog" className="font-bold" data-testid="tab-blog">
              Blog
            </TabsTrigger>
            <TabsTrigger value="contacts" className="font-bold" data-testid="tab-contacts">
              İletişim
            </TabsTrigger>
            <TabsTrigger value="stats" className="font-bold" data-testid="tab-stats">
              İstatistikler
            </TabsTrigger>
            <TabsTrigger value="seo" className="font-bold" data-testid="tab-seo">
              SEO & Ölçüm
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <BookingsManagement />
          </TabsContent>

          <TabsContent value="tours">
            <ToursManagement />
          </TabsContent>

          <TabsContent value="slider">
            <SliderManagement />
          </TabsContent>

          <TabsContent value="blog">
            <BlogManagement />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsManagement />
          </TabsContent>

          <TabsContent value="stats">
            {activeTab === 'stats' && <AnalyticsDashboard />}
          </TabsContent>

          <TabsContent value="seo">
            <SeoSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}